import { Search } from "@mui/icons-material";
import { InputAdornment } from "@mui/material";
import React, { FC } from "react";
import DeleteIcon from "../../../../../assets/images/delete-icon.svg";
import Edit from "../../../../../assets/images/edit.svg";
import {
  FormController,
  MuiBox,
  MuiGrid,
  MuiLink,
  MuiOptionMenu,
  MuiSelect,
  MuiTable,
  MuiTextField,
  OptionsDataType,
  RowProps,
} from "../../../../../components";
import MuiChipWithPopover from "../../../../../components/chipWithPopover/MuiChipWithPopover";
import MuiEllipseLabel from "../../../../../components/ellipseLabel/MuiEllipseLabel";
import PermissionWrapper from "../../../../../components/permissionWrapper/PermissionWrapper";
import { StatusBadge } from "../../../../../components/statusBadge/StatusBadge";
import {
  MAX_LENGTH_50,
  MODULE_PERMISSIONS,
} from "../../../../../helpers/constants";
import { buildActionMenuItems, isNotEmpty } from "../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { usePermissions } from "../../../../../hooks/usePermissions";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import {
  ProgramTemplateActivitiesFormKey,
  ProgramTemplateActivitiesTabProps,
} from "../../../modelsAndSchemas/Models";
import { createProgramSiteCoreKeys } from "../../../siteCore/SiteCoreKeys";
export const pageId = "Create-Program-Template-Activities-tab";

export const CreateProgramTemplateActivitiesTab: FC<
  ProgramTemplateActivitiesTabProps
> = ({
  rows,
  control,
  tableRef,
  isViewTemplate,
  uniqueFilters,
  filteredResults,
  onActivityViewClick,
  deleteAction,
}) => {
  const { fetchLabel, commonLabels, fetchDropdownValues } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { hasModulePermission } = usePermissions();

  const renderFormControl = (
    control: any,
    name: keyof typeof ProgramTemplateActivitiesFormKey,
    options: OptionsDataType[],
    placeholder: string,
    id: string,
  ) => (
    <FormController control={control} name={name} key={name}>
      <MuiSelect
        label=""
        visibleDefaultSelectOption={true}
        options={options}
        value=""
        id={id}
        placeholder={placeholder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>
  );

  const tableHeaderForm = [
    <FormController
      control={control}
      name={ProgramTemplateActivitiesFormKey.activityType}
      key={ProgramTemplateActivitiesFormKey.activityType}
    >
      <MuiTextField
        id={`${pageId}-${ProgramTemplateActivitiesFormKey.activityType}`}
        startAdornment={
          <InputAdornment position="start">
            <Search />
          </InputAdornment>
        }
        aria-label={commonLabels.search}
        showCloseIcon={true}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    renderFormControl(
      control,
      ProgramTemplateActivitiesFormKey.modality,
      uniqueFilters.modality,
      commonLabels.selectPlaceHolder,
      `${pageId}-${ProgramTemplateActivitiesFormKey.modality}`,
    ),
    <FormController
      control={control}
      name={ProgramTemplateActivitiesFormKey.topic}
      key={ProgramTemplateActivitiesFormKey.topic}
    >
      <MuiTextField
        id={`${pageId}-${ProgramTemplateActivitiesFormKey.topic}`}
        startAdornment={
          <InputAdornment position="start">
            <Search />
          </InputAdornment>
        }
        aria-label={commonLabels.search}
        showCloseIcon={true}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    renderFormControl(
      control,
      ProgramTemplateActivitiesFormKey.activityStatus,
      fetchDropdownValues(
        createProgramSiteCoreKeys.activitiesList,
        createProgramSiteCoreKeys.listStatusList,
        true,
      ),
      commonLabels.selectPlaceHolder,
      `${pageId}-${ProgramTemplateActivitiesFormKey.activityStatus}`,
    ),
    renderFormControl(
      control,
      ProgramTemplateActivitiesFormKey.programFramework,
      uniqueFilters.programFramework,
      commonLabels.selectPlaceHolder,
      `${pageId}-${ProgramTemplateActivitiesFormKey.programFramework}`,
    ),
    renderFormControl(
      control,
      ProgramTemplateActivitiesFormKey.units,
      uniqueFilters.units,
      commonLabels.selectPlaceHolder,
      `${pageId}-${ProgramTemplateActivitiesFormKey.units}`,
    ),
    <></>,
    renderFormControl(
      control,
      ProgramTemplateActivitiesFormKey.candidateStatus,
      fetchDropdownValues(
        createProgramSiteCoreKeys.activitiesList,
        createProgramSiteCoreKeys.listCandidateStatusList,
        true,
      ),
      commonLabels.selectPlaceHolder,
      `${pageId}-${ProgramTemplateActivitiesFormKey.candidateStatus}`,
    ),
    <></>,
  ];

  const columns = [
    {
      id: ProgramTemplateActivitiesFormKey.activityType,
      label: fetchLabel(
        createProgramSiteCoreKeys.activitiesList,
        createProgramSiteCoreKeys.activityTypeHeader,
      ),
      render: (row: any) =>
        !row.activityType ? null : (
          <MuiLink
            onClick={() =>
              hasModulePermission(
                MODULE_PERMISSIONS.VIEW_ACTIVITIES_AND_DELIVERABLES,
              ) && onActivityViewClick(row)
            }
          >
            <MuiBox>
              <MuiEllipseLabel
                labelTypoClassName="Link"
                text={row.activityType}
                labelTypoVariant="h3"
                placement="bottom-start"
                pointerCursor
              />
            </MuiBox>
          </MuiLink>
        ),
      isSortVisible: true,
      className: "cell-activity-type",
    },
    {
      id: ProgramTemplateActivitiesFormKey.modality,
      label: fetchLabel(
        createProgramSiteCoreKeys.activitiesList,
        createProgramSiteCoreKeys.modalityHeader,
      ),
      isSortVisible: true,
      className: "cell-modality",
      render: (row: RowProps) => (
        <MuiChipWithPopover
          data={row.modality?.map((item: string) => ({
            label: item,
            value: item,
          }))}
          id={"selected-modality"}
          isEditable={false}
          headerLabel={fetchLabel(
            createProgramSiteCoreKeys.activitiesList,
            createProgramSiteCoreKeys.modalityHeader,
          )}
          isDialogTitleWithEllipsis
          gridRef={React.createRef<HTMLElement>()}
          countToWrap={1}
          showMoreARIALabel={commonARIALabel.showMoreDetails}
          removeChipNameARIALabel={commonARIALabel.removeChipName}
          closePopoverARIALabel={commonARIALabel.closePopover}
        />
      ),
    },
    {
      id: ProgramTemplateActivitiesFormKey.topic,
      label: fetchLabel(
        createProgramSiteCoreKeys.activitiesList,
        createProgramSiteCoreKeys.topicHeader,
      ),
      isSortVisible: true,
      className: "cell-topics",
    },
    {
      id: ProgramTemplateActivitiesFormKey.activityStatus,
      label: fetchLabel(
        createProgramSiteCoreKeys.activitiesList,
        createProgramSiteCoreKeys.activityStatusHeader,
      ),
      isSortVisible: true,
      className: "cell-activity-status",
      render: (row: any) => (
        <StatusBadge
          status={row.activityStatusKey}
          label={row.activityStatus}
        />
      ),
    },
    {
      id: ProgramTemplateActivitiesFormKey.programFramework,
      label: fetchLabel(
        createProgramSiteCoreKeys.activitiesList,
        createProgramSiteCoreKeys.programFramework,
      ),
      isSortVisible: true,
      className: "cell-program-framework",
    },
    {
      id: ProgramTemplateActivitiesFormKey.units,
      label: fetchLabel(
        createProgramSiteCoreKeys.activitiesList,
        createProgramSiteCoreKeys.unitsHeader,
      ),
      isSortVisible: true,
      className: "cell-units",
    },
    {
      id: ProgramTemplateActivitiesFormKey.measurement,
      label: fetchLabel(
        createProgramSiteCoreKeys.activitiesList,
        createProgramSiteCoreKeys.measurementHeader,
      ),
      isSortVisible: true,
      className: "cell-measurement",
    },
    {
      id: ProgramTemplateActivitiesFormKey.candidateStatus,
      label: fetchLabel(
        createProgramSiteCoreKeys.activitiesList,
        createProgramSiteCoreKeys.candidateStatusHeader,
      ),
      isSortVisible: true,
      className: "cell-candidate-status",
      render: (row: RowProps) =>
        isNotEmpty(row.candidateStatus) ? (
          <MuiChipWithPopover
            data={row.candidateStatus?.map((item: string) => ({
              label: item,
              value: item,
            }))}
            id={"selected-candidate-status"}
            isEditable={false}
            headerLabel={fetchLabel(
              createProgramSiteCoreKeys.activitiesList,
              createProgramSiteCoreKeys.candidateStatusHeader,
            )}
            isDialogTitleWithEllipsis
            gridRef={React.createRef<HTMLElement>()}
            countToWrap={1}
            showMoreARIALabel={commonARIALabel.showMoreDetails}
            removeChipNameARIALabel={commonARIALabel.removeChipName}
            closePopoverARIALabel={commonARIALabel.closePopover}
          />
        ) : null,
    },

    {
      id: "action",
      label: fetchLabel(
        createProgramSiteCoreKeys.activitiesList,
        createProgramSiteCoreKeys.actionHeader,
      ),
      render: (row: any) => {
        const activityActions = [
          {
            permissionKey: MODULE_PERMISSIONS.EDIT_ACTIVITIES_AND_DELIVERABLES,
            create: () => ({
              onClick: () => onActivityViewClick(row, true),
              dataTestId: "edit",
              label: commonLabels.edit,
            }),
          },
        ];

        if (!row?.programFrameworkId) {
          activityActions.push({
            permissionKey:
              MODULE_PERMISSIONS.DELETE_ACTIVITIES_AND_DELIVERABLES,
            create: () => ({
              onClick: () => deleteAction(row),
              dataTestId: "delete-button",
              label: commonLabels.delete,
            }),
          });
        }

        const menuItems = buildActionMenuItems(
          row,
          activityActions,
          hasModulePermission,
        );

        return menuItems ? <MuiOptionMenu menuItems={menuItems} /> : null;
      },
    },
  ];

  return (
    <MuiGrid container data-testid="search-activities">
      <MuiGrid size={{ xs: 12 }} className="header-right">
        <MuiTable
          pageId={pageId}
          tableHeaderForm={
            isViewTemplate ? tableHeaderForm.slice(0, -1) : tableHeaderForm
          }
          ref={tableRef}
          noDataInApi={rows.length === 0}
          tableResults={[...filteredResults]}
          priorityResults={[]}
          isLastColumnSticky={!isViewTemplate}
          columns={isViewTemplate ? columns.slice(0, -1) : columns}
          noResultMessage={commonLabels.noResult}
          emptyMessage={fetchLabel(
            createProgramSiteCoreKeys.activitiesList,
            createProgramSiteCoreKeys.activitiesNotAvailable,
          )}
        />
      </MuiGrid>
    </MuiGrid>
  );
};
